import { useNavigate } from "react-router-dom";
import { toast } from "@/state/hooks";
import {
  Phone,
  Users,
  UserPlus,
  LogOut,
  Search,
  Trash2,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/ui/components/ui/dropdown-menu";
import { useAppDialog } from "@/state/legacy_module_api";
import { useActions, useAppState } from "@/state/legacy_module_api";

interface ThreadViewMenuPopOverProps {
  receiverId?: string;
  senderId?: string;
  groupId?: string;
  onCallClick?: () => void;
  onSearchClick?: () => void;
  onShowMembers?: () => void;
  onAddMember?: () => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trigger: React.ReactNode;
}

export const ThreadViewMenuPopOver = ({
  receiverId,
  senderId,
  groupId,
  onCallClick,
  onSearchClick,
  onShowMembers,
  onAddMember,
  open,
  onOpenChange,
  trigger,
}: ThreadViewMenuPopOverProps) => {
  const navigate = useNavigate();
  const { openDialog } = useAppDialog();
  const group = useAppState().group;
  const groupActions = useActions().group as any;
  const exitGroup = groupActions?.exitGroup || (async () => {});
  const threadActions = useActions().thread as any;
  const startCall = threadActions?.startCall || (() => {});
  const clearAllMessages = threadActions?.clearAllMessages || (async () => {});
  const { selectedThread } = useAppState().thread;
  const myUserId = (useAppState() as any).myUserId;
  const thread = selectedThread?.threadKey ? group.threadMap[selectedThread.threadKey] : undefined;
  const isSelfAdmin = thread ? thread.ownerId === myUserId : false;
  // Check if this is a real group (thread key starts with "group:")
  const isGroupThread = !!selectedThread?.threadKey?.startsWith("group:");

  const handleExitGroup = async () => {
    try {
      if (isSelfAdmin) {
        toast.error("You cannot exit a group you own.");
        return;
      }
      await exitGroup({ groupId: selectedThread?.groupId });
      navigate(-1);
    } catch (error) {
      console.error("Error exiting group:", error);
      toast.error("Error exiting group");
    }
  };

  const handleClearMessages = () => {
    openDialog({
      type: "confirm",
      title: "Clear All Messages",
      message:
        "Are you sure you want to delete all messages in this thread? This action cannot be undone.",
      onConfirm: async () => {
        try {
          await clearAllMessages();
          toast.success("All messages cleared successfully");
        } catch (error) {
          console.error("Error clearing messages:", error);
          toast.error("Error clearing messages");
        }
      },
    });
  };

  return (
    <DropdownMenu open={open} onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuItem
          onClick={() => {
            onOpenChange(false);
            if (onCallClick) {
              onCallClick();
              return;
            }
            startCall();
          }}
        >
          <Phone className="w-4 h-4 mr-2" />
          Call
        </DropdownMenuItem>

        {isGroupThread && (
          <DropdownMenuItem
            onClick={() => {
              onOpenChange(false);
              onShowMembers?.();
            }}
          >
            <Users className="w-4 h-4 mr-2" />
            Details
          </DropdownMenuItem>
        )}

        {isGroupThread && isSelfAdmin && (
          <DropdownMenuItem
            onClick={() => {
              onOpenChange(false);
              onAddMember?.();
            }}
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Add Member
          </DropdownMenuItem>
        )}

        {isGroupThread && !isSelfAdmin && (
          <DropdownMenuItem
            onClick={handleExitGroup}
            className="text-destructive"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Exit Group
          </DropdownMenuItem>
        )}

        <DropdownMenuItem
          onClick={() => {
            onOpenChange(false);
            onSearchClick?.();
          }}
        >
          <Search className="w-4 h-4 mr-2" />
          Search Messages
        </DropdownMenuItem>

        <DropdownMenuItem
          onClick={handleClearMessages}
          className="text-destructive"
        >
          <Trash2 className="w-4 h-4 mr-2" />
          Clear Messages
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
